/* eslint-disable react-refresh/only-export-components */
import {createContext, useCallback, useContext, useEffect, useState} from 'react'
import toast from 'react-hot-toast'
import {useTranslation} from 'react-i18next'
import { apiClient } from '../services/apiClient'
import {useUser} from './UserContext'
import type {AuthContextValue, LoginResult, RegisterResult, DeleteAccountResult, User} from '../types/auth'
import React from 'react'
import {analyticsService} from '../services/analyticsService'
import {syncLanguageFromUser} from '../i18n'

const AuthContext = createContext<AuthContextValue | null>(null)

export const AuthProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [isInitializing, setIsInitializing] = useState(true)
    const {t} = useTranslation('auth')
    const {clearUser, updateUser} = useUser()

    // Load user and verify authentication on mount
    useEffect(() => {
        let isMounted = true
        const abortController = new AbortController()

        const loadUser = async () => {
            try {
                const response = await apiClient.get('/users/me', {
                    signal: abortController.signal
                })

                // Only update state if component is still mounted
                if (isMounted) {
                    const loadedUser = response.data as User
                    updateUser(loadedUser)
                    setIsAuthenticated(true)
                    analyticsService.identify(loadedUser.id, loadedUser.tenantId ?? null)
                    syncLanguageFromUser(loadedUser)
                }
            } catch (error) {
                const err = error as {name?: string}
                // Don't handle aborted requests
                if (err.name === 'AbortError' || err.name === 'CanceledError') {
                    return
                }

                // No valid authentication cookie found
                if (isMounted) {
                    clearUser()
                }
            }

            if (isMounted) {
                setIsLoading(false)
                setIsInitializing(false)
            }
        }

        loadUser()

        return () => {
            isMounted = false
            abortController.abort()
        }
    }, [clearUser, updateUser])

    const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
        try {
            const response = await apiClient.post('/auth/login', {email, password})
            const {user} = response.data as {user: User}
            updateUser(user)
            setIsAuthenticated(true)
            analyticsService.identify(user.id, user.tenantId ?? null)
            return {success: true, user}
        } catch (error) {
            const err = error as {response?: {status?: number; data?: {message?: string}}}
            const message = err.response?.status === 429
                ? t('too_many_attempts')
                : err.response?.data?.message || t('login_failed')
            return {success: false, error: message}
        }
    }, [updateUser, t])

    const register = useCallback(async (userData: Record<string, unknown>): Promise<RegisterResult> => {
        try {
            const response = await apiClient.post('/auth/register', userData, { suppressErrorToast: true } as Record<string, unknown>)
            const {success, message, email, requiresVerification} = response.data as {
                success: boolean;
                message?: string;
                email?: string;
                requiresVerification?: boolean;
            }

            if (success && requiresVerification) {
                return {
                    success: true,
                    requiresVerification: true,
                    email,
                    message
                }
            }

            toast.success(t('auth:registration_successful', 'Registration successful!'))
            return {success: true}
        } catch (error) {
            const err = error as {response?: {status?: number; data?: {message?: string}}}
            const message = err.response?.status === 429
                ? t('too_many_attempts')
                : err.response?.data?.message || 'Registration failed'
            toast.error(message)
            return {success: false, error: message}
        }
    }, [t])

    const resetAuthState = useCallback(() => {
        analyticsService.reset()
        clearUser()
        setIsAuthenticated(false)
    }, [clearUser])

    const logout = useCallback(async (): Promise<void> => {
        try {
            await apiClient.post('/auth/logout')
        } catch (error) {
            console.error('Logout error:', error)
        } finally {
            analyticsService.track('auth.logout')
            resetAuthState()
        }
    }, [resetAuthState])

    const deleteAccount = async (): Promise<DeleteAccountResult> => {
        try {
            const response = await apiClient.delete('/users/me')
            resetAuthState()
            toast.success(t('auth:account_deleted_successfully', 'Account deleted successfully'))

            return {success: true, message: (response.data as {message?: string}).message}
        } catch (error) {
            console.error('Account deletion error:', error)
            const err = error as {response?: {data?: {message?: string}}}
            const message = err.response?.data?.message || t('auth:account_deletion_failed', 'Failed to delete account')
            toast.error(message)
            return {success: false, error: message}
        }
    }

    const value: AuthContextValue = {
        isAuthenticated,
        isLoading,
        isInitializing,
        login,
        register,
        logout,
        deleteAccount,
        setIsAuthenticated
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextValue => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
